import React, { useCallback, useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import CardPreview from './CardPreview';
import Controls from './Controls';
import HeadPhotoModal from './HeadPhotoModal';
import JerseySelector from './JerseySelector';
import HeadPlacementTweaks from './HeadPlacementTweaks';
import './playerCard.css';

const INITIAL_JERSEY = 'jersey1';

export default function PlayerCardGenerator() {
  const cardRef = useRef(null);
  const [selectedJersey, setSelectedJersey] = useState(INITIAL_JERSEY);
  const [playerName, setPlayerName] = useState('');
  const [playerNumber, setPlayerNumber] = useState('');
  const [playerPosition, setPlayerPosition] = useState('');
  const [uploadedHeadImage, setUploadedHeadImage] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [headModalOpen, setHeadModalOpen] = useState(false);
  const [headShiftX, setHeadShiftX] = useState(0);
  const [headShiftY, setHeadShiftY] = useState(0);
  const [headSizePct, setHeadSizePct] = useState(100);

  const headUrlRef = useRef(null);
  useEffect(() => {
    headUrlRef.current = uploadedHeadImage;
  }, [uploadedHeadImage]);

  useEffect(() => {
    return () => {
      if (headUrlRef.current) URL.revokeObjectURL(headUrlRef.current);
    };
  }, []);

  const resetHeadPlacement = useCallback(() => {
    setHeadShiftX(0);
    setHeadShiftY(0);
    setHeadSizePct(100);
  }, []);

  const prevHeadUrl = useRef(null);
  useEffect(() => {
    if (uploadedHeadImage === prevHeadUrl.current) return;
    const prev = prevHeadUrl.current;
    prevHeadUrl.current = uploadedHeadImage;
    if (uploadedHeadImage && uploadedHeadImage !== prev) {
      resetHeadPlacement();
    }
  }, [uploadedHeadImage, resetHeadPlacement]);

  const handleDownload = useCallback(async () => {
    const el = cardRef.current;
    if (!el || !uploadedHeadImage || !playerName.trim()) return;

    setIsDownloading(true);
    try {
      const scale = Math.min(3, (typeof window !== 'undefined' && window.devicePixelRatio) || 2);
      const canvas = await html2canvas(el, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: el.offsetWidth,
        height: el.offsetHeight,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });

      await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('PNG export failed'));
              return;
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'gladiator-card.png';
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2500);
            resolve();
          },
          'image/png',
          1
        );
      });
    } catch (err) {
      console.error('Download failed', err);
    } finally {
      setIsDownloading(false);
    }
  }, [playerName, uploadedHeadImage]);

  const handleHeadApplyFromCrop = useCallback((blobUrl) => {
    setUploadedHeadImage((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return blobUrl;
    });
  }, []);

  const downloadDisabled = !uploadedHeadImage || !playerName.trim() || isDownloading;

  return (
    <div className="player-card-generator pcg-root">
      <header className="pcg-header">
        <h1 className="pcg-title">Build Your Gladiator</h1>
        <p className="pcg-lead">
          Pick a jersey template, add your name and number, add your head with the camera, then download your player
          card.
        </p>
      </header>

      <div className={`pcg-layout${uploadedHeadImage ? ' pcg-layout--with-head' : ''}`}>
        <div className="pcg-area-card">
          <section className="pcg-preview-col">
            <h2 className="visually-hidden">Preview</h2>
            <CardPreview
              ref={cardRef}
              selectedJersey={selectedJersey}
              uploadedHeadImage={uploadedHeadImage}
              playerNumber={playerNumber}
              playerName={playerName}
              playerPosition={playerPosition}
              headShiftX={headShiftX}
              headShiftY={headShiftY}
              headSizePct={headSizePct}
              onHeadShiftX={setHeadShiftX}
              onHeadShiftY={setHeadShiftY}
              onHeadSizePct={setHeadSizePct}
            />
          </section>
        </div>

        {uploadedHeadImage ? (
          <aside className="pcg-area-placement" aria-label="Adjust head on card">
            <HeadPlacementTweaks
              shiftX={headShiftX}
              shiftY={headShiftY}
              sizePct={headSizePct}
              onShiftX={setHeadShiftX}
              onShiftY={setHeadShiftY}
              onSizePct={setHeadSizePct}
              onReset={resetHeadPlacement}
            />
          </aside>
        ) : null}

        <section className="pcg-controls-col">
          <h2 className="visually-hidden">Customize</h2>
          <JerseySelector selectedJersey={selectedJersey} onSelectJersey={setSelectedJersey} />
          <Controls
            playerName={playerName}
            playerNumber={playerNumber}
            playerPosition={playerPosition}
            onNameChange={setPlayerName}
            onNumberChange={setPlayerNumber}
            onPositionChange={setPlayerPosition}
            onOpenCamera={() => setHeadModalOpen(true)}
            onDownload={handleDownload}
            isDownloading={isDownloading}
            downloadDisabled={downloadDisabled}
          />
        </section>
      </div>

      <HeadPhotoModal
        open={headModalOpen}
        onClose={() => setHeadModalOpen(false)}
        onApply={handleHeadApplyFromCrop}
        busy={isDownloading}
      />
    </div>
  );
}
